import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { GOOGLE_MAPS_LOADER_OPTIONS } from './MapGisTools';
import { useMapsUnavailable, MapUnavailable } from './MapUnavailable';
import { MapPin, Search } from 'lucide-react';

const DEFAULT_CENTER = { lat: 24.7136, lng: 46.6753 }; // Riyadh, SA

// Pick a zone's GPS coordinates by searching Google Maps or dropping/dragging a
// pin. Falls back to manual lat/lng inputs when the Maps key is unavailable.
export const ZoneGpsPicker = ({ value, onChange, biasCenter = null, biasRadiusKm = 250, countryIso2 = '', readOnly = false }) => {
  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);
  const mapsUnavailable = useMapsUnavailable();
  const sessionTokenRef = useRef(null);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [map, setMap] = useState(null);

  const hasVal = value && Number.isFinite(Number(value.lat)) && Number.isFinite(Number(value.lng));
  const preferredCenter = biasCenter && Number.isFinite(Number(biasCenter.lat)) && Number.isFinite(Number(biasCenter.lng))
    ? { lat: Number(biasCenter.lat), lng: Number(biasCenter.lng) }
    : DEFAULT_CENTER;
  const center = hasVal ? { lat: Number(value.lat), lng: Number(value.lng) } : preferredCenter;

  const biasBounds = useMemo(() => {
    if (!isLoaded || !window.google?.maps || !preferredCenter) return null;
    const latDelta = Math.max(0.25, Number(biasRadiusKm || 250) / 111);
    const lngDelta = latDelta / Math.max(0.2, Math.cos((preferredCenter.lat * Math.PI) / 180));
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend({ lat: preferredCenter.lat - latDelta, lng: preferredCenter.lng - lngDelta });
    bounds.extend({ lat: preferredCenter.lat + latDelta, lng: preferredCenter.lng + lngDelta });
    return bounds;
  }, [biasRadiusKm, isLoaded, preferredCenter.lat, preferredCenter.lng]);

  const setPoint = (lat, lng) => {
    if (!readOnly) onChange?.({ lat: Number(Number(lat).toFixed(6)), lng: Number(Number(lng).toFixed(6)) });
  };
  const onMapClick = (e) => !readOnly && e.latLng && setPoint(e.latLng.lat(), e.latLng.lng());

  useEffect(() => {
    if (!map) return;
    map.panTo(center);
    map.setZoom(hasVal ? 16 : 11);
  }, [center.lat, center.lng, hasVal, map]);

  // Use Google's Places Autocomplete Data API (New). The former Autocomplete
  // and Geocoder services are legacy APIs and may be disabled even while Maps
  // and Places (New) are enabled for the project.
  const runAddressSearch = async () => {
    const input = query.trim();
    if (!input || !window.google?.maps?.importLibrary) return;
    setSearching(true);
    setSearchError('');
    setSuggestions([]);
    try {
      const { AutocompleteSuggestion, AutocompleteSessionToken } = await window.google.maps.importLibrary('places');
      if (!sessionTokenRef.current) sessionTokenRef.current = new AutocompleteSessionToken();
      const request = {
        input,
        sessionToken: sessionTokenRef.current,
        region: String(countryIso2 || '').trim().toLowerCase() || undefined,
        locationBias: biasBounds || preferredCenter,
      };
      const response = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      const predictions = (response?.suggestions || []).map((item) => item.placePrediction).filter(Boolean);
      setSuggestions(predictions);
      if (!predictions.length) setSearchError('No matching place found. Try a broader search or drop a pin.');
    } catch (error) {
      const message = String(error?.message || '');
      setSearchError(
        /Places API \(New\).*disabled|Places API \(New\).*not been used/i.test(message)
          ? 'Google place search requires Places API (New) to be enabled for this Maps key. You can still drop a pin.'
          : 'Google place search is unavailable. Drop a pin on the map instead.'
      );
    } finally {
      setSearching(false);
    }
  };

  const selectSuggestion = async (prediction) => {
    setSearching(true);
    setSearchError('');
    try {
      const place = prediction.toPlace();
      await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location'] });
      const loc = place.location;
      if (!loc) throw new Error('Selected place has no coordinates');
      setPoint(loc.lat(), loc.lng());
      setQuery(place.formattedAddress || place.displayName || prediction.text?.toString?.() || query);
      setSuggestions([]);
      sessionTokenRef.current = null;
    } catch (_) {
      setSearchError('That place could not be loaded. Try another result or drop a pin.');
    } finally {
      setSearching(false);
    }
  };

  const manualInputs = (
    <div className="grid grid-cols-2 gap-2 mt-2">
      <input
        type="number" step="any" placeholder="Latitude" data-testid="zone-gps-lat"
        value={value?.lat ?? ''}
        onChange={(e) => onChange({ lat: e.target.value === '' ? null : Number(e.target.value), lng: value?.lng ?? null })}
        className="px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
      />
      <input
        type="number" step="any" placeholder="Longitude" data-testid="zone-gps-lng"
        value={value?.lng ?? ''}
        onChange={(e) => onChange({ lat: value?.lat ?? null, lng: e.target.value === '' ? null : Number(e.target.value) })}
        className="px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
      />
    </div>
  );

  return (
    <div data-testid="zone-gps-picker">
      <label className="text-sm font-medium mb-1 flex items-center gap-1">
        <MapPin className="h-3.5 w-3.5 text-blue-600" /> GPS Location
        <span className="font-normal text-gray-400">{readOnly ? '(selected location preview)' : '(search or drop a pin)'}</span>
      </label>

      {mapsUnavailable ? (
        <>
          <div className="h-48 rounded-lg overflow-hidden border border-gray-200">
            <MapUnavailable
              center={hasVal ? center : null}
              label={readOnly ? 'Selected location map preview' : 'Map search unavailable — enter coordinates below'}
            />
          </div>
          {!readOnly && manualInputs}
        </>
      ) : !isLoaded ? (
        <div className="h-48 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 text-sm">Loading map…</div>
      ) : (
        <>
          {!readOnly && (
            <div className="relative mb-2">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
              <input
                data-testid="zone-gps-search"
                placeholder="Search a place on Google Maps…"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSuggestions([]);
                  setSearchError('');
                }}
                className="w-full rounded-md border border-gray-300 py-2 pl-8 pr-20 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    runAddressSearch();
                  }
                }}
              />
              <button
                type="button"
                onClick={runAddressSearch}
                disabled={searching || !query.trim()}
                className="absolute right-1.5 top-1.5 rounded bg-blue-600 px-2 py-1 text-[11px] font-bold text-white disabled:opacity-50"
              >
                {searching ? 'Searching…' : 'Search'}
              </button>
            </div>
          )}
          {!readOnly && suggestions.length > 0 && (
            <div className="mb-2 overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm" data-testid="zone-gps-search-results">
              {suggestions.map((prediction, index) => (
                <button
                  key={prediction.placeId || `${prediction.text?.toString?.() || 'place'}-${index}`}
                  type="button"
                  onClick={() => selectSuggestion(prediction)}
                  className="block w-full border-b border-gray-100 px-3 py-2 text-left text-xs text-gray-700 last:border-b-0 hover:bg-blue-50"
                >
                  {prediction.text?.toString?.() || 'Google Maps result'}
                </button>
              ))}
            </div>
          )}
          {searchError && <p className="mb-2 text-[11px] font-medium text-amber-700">{searchError}</p>}
          <div className="h-48 rounded-lg overflow-hidden border border-gray-200">
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={center}
              zoom={hasVal ? 16 : 11}
              onClick={onMapClick}
              onLoad={setMap}
              options={{ streetViewControl: false, mapTypeControl: false }}
            >
              {hasVal && (
                <Marker position={center} draggable={!readOnly} onDragEnd={(e) => setPoint(e.latLng.lat(), e.latLng.lng())} />
              )}
            </GoogleMap>
          </div>
          <div className="flex items-center justify-between mt-1.5">
            <p className="text-[11px] text-gray-500">
              {hasVal ? `📍 ${center.lat}, ${center.lng}` : 'Click the map or search to drop a pin.'}
            </p>
            {hasVal && !readOnly && (
              <button type="button" onClick={() => onChange(null)} className="text-[11px] text-red-600 hover:underline" data-testid="zone-gps-clear">Clear</button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ZoneGpsPicker;
